import React from 'react'
import {useTranslation} from '@lib/intl/useTranslations'
import {useRouter} from 'next/router'

import {createLog} from '@util/logs'
import {
    getLinkUrl,
    URL_ACCOUNT_API,
    URL_ACCOUNT_INVOICE,
    URL_ACCOUNT_LANDING_PRO,
    URL_ACCOUNT_NOTIFICATION,
    URL_ACCOUNT_POINT,
    URL_ACCOUNT_PRO,
    URL_ACCOUNT_REFERRAL,
    URL_ACCOUNT_SETTING,
    URL_ACCOUNT_SUBSCRIBER,
    URL_ACCOUNT_WALLET,
} from '@constant/url'

import useFormatNumber from '@hook/useFormatNumber'
import useQueryGetPoint from '@hook/query/account/point/useQueryGetPoint'
import {useImportantAlertsContext} from '@hook/useImportantAlerts'

import Text from '@component/Text/Text'

import IconLeaguePointWhite from '@svg/common/ic_league_point_symbol_white.svg'
import IconLeaguePoint from '@svg/common/ic_league_point_symbol.svg'

interface IProps {
    className?: string
}

const AccountOperationPages = ({className}: IProps) => {
    const t = useTranslation()
    const router = useRouter()
    const {displayNumber} = useFormatNumber()

    const {alertLength, invoiceAlertLength, settingAlertLength, proAlertLength} = useImportantAlertsContext()
    const {data: point} = useQueryGetPoint()

    const menuOptions = [
        {
            title: t('globalNavigation.my.menu.setting'),
            href: getLinkUrl(URL_ACCOUNT_SETTING),
            renderRight:
                settingAlertLength > 0 ? (
                    <div className={'min-w-[14px] px-[4px] h-[14px] rounded-[7px] bg-red_shade dark:bg-dark_red_shade'}>
                        <Text className={'text-ti4 text-white text-center leading-[14px]'}>{settingAlertLength}</Text>
                    </div>
                ) : null,
        },
        {
            title: t('globalNavigation.my.menu.api'),
            renderRight:
                alertLength > 0 ? (
                    <div className={'min-w-[14px] px-[4px] h-[14px] rounded-[7px] bg-red_shade dark:bg-dark_red_shade'}>
                        <Text className={'text-ti4 text-white text-center leading-[14px]'}>{alertLength}</Text>
                    </div>
                ) : null,
            href: getLinkUrl(URL_ACCOUNT_API),
        },
        {
            title: t('globalNavigation.my.menu.wallet'),
            href: getLinkUrl(URL_ACCOUNT_WALLET),
        },
        {
            title: t('globalNavigation.my.menu.invoice'),
            renderRight:
                invoiceAlertLength > 0 ? (
                    <div className={'min-w-[14px] px-[4px] h-[14px] rounded-[7px] bg-red_shade dark:bg-dark_red_shade'}>
                        <Text className={'text-ti4 text-white text-center leading-[14px]'}>{invoiceAlertLength}</Text>
                    </div>
                ) : null,
            href: getLinkUrl(URL_ACCOUNT_INVOICE),
        },
        {
            title: t('globalNavigation.my.menu.subscriber'),
            href: getLinkUrl(URL_ACCOUNT_SUBSCRIBER),
        },
        {
            title: t('globalNavigation.my.menu.notifications'),
            href: getLinkUrl(URL_ACCOUNT_NOTIFICATION),
        },
        {
            title: t('globalNavigation.my.menu.referral'),
            href: getLinkUrl(URL_ACCOUNT_REFERRAL),
        },
        {
            title: t('globalNavigation.my.menu.pro'),
            className: '!text-ti2 text-ti2 bg-gradient_pro !text-transparent bg-clip-text flex-1',
            renderRight:
                proAlertLength > 0 ? (
                    <div className={'min-w-[14px] px-[4px] h-[14px] rounded-[7px] bg-red_shade dark:bg-dark_red_shade'}>
                        <Text className={'text-ti4 text-white text-center leading-[14px]'}>{proAlertLength}</Text>
                    </div>
                ) : null,
            renderTag: (
                <div
                    onClick={e => {
                        router?.push(getLinkUrl(URL_ACCOUNT_LANDING_PRO))
                        e.stopPropagation()

                        if (e?.cancelable) {
                            e.preventDefault()
                        }
                    }}
                    className={'flex-1 text-right'}>
                    <Text className={'text-body_comm underline text-gray01 dark:text-dark_gray01 '}>
                        {t('globalNavigation.my.menu.pro.more')}
                    </Text>
                </div>
            ),
            href: getLinkUrl(URL_ACCOUNT_PRO),
        },
        {
            title: t('globalNavigation.my.menu.point'),
            className: 'flex-1',
            renderLeft: (
                <>
                    <IconLeaguePointWhite className={'w-[16px] h-[16px] hidden dark:block flex-none'} />
                    <IconLeaguePoint className={'w-[16px] h-[16px] fill-black dark:hidden flex-none'} />
                </>
            ),
            renderTag: (
                <>
                    <Text className={'flex-1 text-right text-body_comm text-gray01 dark:text-dark_gray01 underline'}>
                        {t('point.text', {point: displayNumber(point?.current_point || 0)})}
                    </Text>
                </>
            ),
            href: getLinkUrl(URL_ACCOUNT_POINT),
        },
    ]

    const sendToPage = (url: string, menu: string) => {
        createLog('event', 'navigation_click_menu', {menu})
        router?.push(url)
    }

    return (
        <div className={`py-[5px]`}>
            {menuOptions.map(option => {
                return (
                    <div
                        key={option.title}
                        onClick={() => sendToPage(option.href, option.title)}
                        className={
                            'cursor-pointer hover:bg-gray07 dark:hover:bg-dark_gray07 transition-colors duration-100'
                        }>
                        <div className={'flex items-center gap-x-[5px] py-[7px] px-[30px]'}>
                            {option?.renderLeft}
                            <div className={'flex items-center gap-x-[5px]'}>
                                <Text className={`text-body2 text-gray02 dark:text-dark_gray02 ${option?.className}`}>
                                    {option?.title}
                                </Text>
                                {option?.renderRight}
                            </div>
                            {option?.renderTag}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
export default AccountOperationPages
